import { styled } from '@mui/material';
import { useState } from 'react';

import {
  Badge,
  Button,
  Chip,
  Loader,
  Modal,
  Separator,
  TextArea,
  theme,
  Typography,
} from '@/shared/design-system';

import { describeRoleRequest, formatRoleRequestSummary } from './role-request-summary';
import { RoleRequestStatus, type RoleRequestView } from './types';

type RoleRequestsListProps = {
  requests: RoleRequestView[];
  status: RoleRequestStatus;
  pendingCount: number;
  canApprove: boolean;
  isLoading: boolean;
  isError: boolean;
  isDecisionPending: boolean;
  onStatusChange: (_status: RoleRequestStatus) => void;
  onApprove: (_requestId: string) => void;
  onReject: (_requestId: string, _reason: string) => void;
  onRetry: () => void;
};

const statusLabel: Record<RoleRequestStatus, string> = {
  [RoleRequestStatus.Pending]: 'Pending',
  [RoleRequestStatus.Approved]: 'Approved',
  [RoleRequestStatus.Rejected]: 'Rejected',
};

const statusColor = {
  [RoleRequestStatus.Pending]: 'yellow',
  [RoleRequestStatus.Approved]: 'secondary',
  [RoleRequestStatus.Rejected]: 'destructive',
} as const;

/** `src/styles/overrides/stack.ts` forces `MuiStack` to `row`, so these wrap `div`, not `Stack`. */

const Column = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
});

const Row = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
});

const Filters = styled(Row)({
  gap: theme.spacing[8],
  padding: `${theme.spacing[20]} 0 ${theme.spacing[24]}`,
});

const RequestsStack = styled(Column)({
  gap: theme.spacing[12],
});

const RequestCard = styled(Column)({
  gap: theme.spacing[12],
  padding: theme.spacing[20],
  border: `1px solid ${theme.colors.border.subtle}`,
  borderRadius: theme.radius[12],
  backgroundColor: theme.colors.background.raised,
});

const RequestHeader = styled(Row)({
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing[16],
});

const HeaderText = styled(Column)({
  gap: theme.spacing[4],
});

const Badges = styled(Row)({
  justifyContent: 'flex-end',
  gap: theme.spacing[8],
});

const RequestFooter = styled(Row)({
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: theme.spacing[16],
});

const MetaGrid = styled(Row)({
  columnGap: theme.spacing[32],
  rowGap: theme.spacing[12],
});

const MetaColumn = styled(Column)({
  gap: theme.spacing[2],
});

const RequestActions = styled(Row)({
  justifyContent: 'flex-end',
  gap: theme.spacing[8],
  marginLeft: 'auto',
});

const ErrorState = styled(Column)({
  alignItems: 'flex-start',
  gap: theme.spacing[8],
});

const PermissionChips = styled(Row)({
  alignItems: 'center',
  gap: theme.spacing[6],
});

/** Enough to read a typical request at a glance without turning a duplicated role into a wall. */
const PERMISSION_PREVIEW_COUNT = 6;

const PermissionList = ({ permissions }: { permissions: string[] }) => {
  const [expanded, setExpanded] = useState(false);
  const hidden = permissions.length - PERMISSION_PREVIEW_COUNT;
  const visible = expanded ? permissions : permissions.slice(0, PERMISSION_PREVIEW_COUNT);

  return (
    <MetaColumn>
      <Typography variant="body4" color={theme.colors.text.tertiary}>
        {permissions.length === 1 ? 'Permission' : `Permissions (${permissions.length})`}
      </Typography>
      <PermissionChips>
        {visible.map((permission) => (
          <Chip key={permission} size="sm" roundness="round" label={permission} />
        ))}
        {hidden > 0 && (
          <Chip
            size="sm"
            roundness="round"
            label={expanded ? 'Show less' : `+${hidden} more`}
            onClick={() => setExpanded((open) => !open)}
          />
        )}
      </PermissionChips>
    </MetaColumn>
  );
};

const formatDate = (value: string | null) =>
  value
    ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
        new Date(value),
      )
    : null;

const MetaField = ({ label, value }: { label: string; value: string }) => (
  <MetaColumn>
    <Typography variant="body4" color={theme.colors.text.tertiary}>
      {label}
    </Typography>
    <Typography variant="body4" color={theme.colors.text.primary}>
      {value}
    </Typography>
  </MetaColumn>
);

const decisionLabel = (status: RoleRequestStatus) =>
  status === RoleRequestStatus.Rejected ? 'Rejected by' : 'Approved by';

export const RoleRequestsList = ({
  requests,
  status,
  pendingCount,
  canApprove,
  isLoading,
  isError,
  isDecisionPending,
  onStatusChange,
  onApprove,
  onReject,
  onRetry,
}: RoleRequestsListProps) => {
  const [rejectionTarget, setRejectionTarget] = useState<RoleRequestView | null>(null);
  const [reason, setReason] = useState('');

  const closeRejectionModal = () => {
    setRejectionTarget(null);
    setReason('');
  };

  const submitRejection = () => {
    const trimmedReason = reason.trim();
    if (!rejectionTarget || !trimmedReason) return;

    onReject(rejectionTarget.id, trimmedReason);
    closeRejectionModal();
  };

  const chipLabel = (value: RoleRequestStatus) =>
    value === RoleRequestStatus.Pending && pendingCount > 0
      ? `${statusLabel[value]} (${pendingCount})`
      : statusLabel[value];

  return (
    <>
      <Filters role="group" aria-label="Filter requests by status">
        {Object.values(RoleRequestStatus).map((value) => (
          <Chip
            key={value}
            size="sm"
            roundness="round"
            label={chipLabel(value)}
            selected={value === status}
            onClick={() => onStatusChange(value)}
          />
        ))}
      </Filters>

      <RequestsStack>
        {isLoading && <Loader />}

        {isError && (
          <ErrorState>
            <Typography variant="body3" color={theme.colors.text.secondary}>
              Couldn&apos;t load {statusLabel[status].toLowerCase()} requests.
            </Typography>
            <Button size="sm" variant="outline" onClick={onRetry}>
              Try again
            </Button>
          </ErrorState>
        )}

        {!isLoading && !isError && requests.length === 0 && (
          <Typography variant="body3" color={theme.colors.text.secondary}>
            No {statusLabel[status].toLowerCase()} requests.
          </Typography>
        )}

        {!isLoading &&
          !isError &&
          requests.map((request) => {
            const { action, detail, context, permissions } = describeRoleRequest(request);
            const decidedDate = formatDate(request.decidedAt);
            const decidable = canApprove && request.status === RoleRequestStatus.Pending;

            return (
              <RequestCard key={request.id}>
                <RequestHeader>
                  <HeaderText>
                    <Typography variant="body4" color={theme.colors.text.tertiary}>
                      {action}
                    </Typography>
                    <Typography variant="body2" color={theme.colors.text.primary}>
                      {detail}
                    </Typography>
                    {context && (
                      <Typography variant="body4" color={theme.colors.text.secondary}>
                        {context}
                      </Typography>
                    )}
                  </HeaderText>
                  <Badges>
                    <Badge
                      size="sm"
                      variant="subtle"
                      color={statusColor[request.status]}
                      label={statusLabel[request.status]}
                    />
                    {request.autoApproved && (
                      <Badge size="sm" variant="subtle" color="secondary" label="Auto-approved" />
                    )}
                  </Badges>
                </RequestHeader>

                {permissions.length > 0 && <PermissionList permissions={permissions} />}

                <Separator tone="subtle" />

                <RequestFooter>
                  <MetaGrid>
                    {request.targetLabel && (
                      <MetaField label="Member" value={request.targetLabel} />
                    )}
                    <MetaField label="Requested by" value={request.initiatorLabel} />
                    <MetaField label="Raised" value={formatDate(request.initiatedAt) ?? '—'} />
                    {request.deciderLabel && decidedDate && (
                      <>
                        <MetaField
                          label={decisionLabel(request.status)}
                          value={request.deciderLabel}
                        />
                        <MetaField label="Decided" value={decidedDate} />
                      </>
                    )}
                  </MetaGrid>

                  {decidable && (
                    <RequestActions>
                      <Button
                        size="md"
                        variant="outline"
                        color="destructive"
                        onClick={() => {
                          setRejectionTarget(request);
                          setReason('');
                        }}
                        disabled={isDecisionPending}
                      >
                        Reject
                      </Button>
                      <Button
                        size="md"
                        onClick={() => onApprove(request.id)}
                        disabled={isDecisionPending}
                      >
                        Approve
                      </Button>
                    </RequestActions>
                  )}
                </RequestFooter>

                {request.rejectionReason && (
                  <MetaField label="Reason" value={request.rejectionReason} />
                )}
              </RequestCard>
            );
          })}
      </RequestsStack>

      <Modal
        open={rejectionTarget !== null}
        onOpenChange={(open) => !open && closeRejectionModal()}
        title="Reject request"
        description={rejectionTarget ? formatRoleRequestSummary(rejectionTarget) : undefined}
        primaryAction={{
          label: 'Reject request',
          color: 'destructive',
          disabled: !reason.trim() || isDecisionPending,
          onClick: submitRejection,
        }}
        secondaryAction={{ label: 'Cancel', onClick: closeRejectionModal }}
      >
        <TextArea
          label="Reason"
          aria-label="Reason"
          required
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Explain why this request is being rejected"
          rows={4}
        />
      </Modal>
    </>
  );
};
